import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services';
import { setUserInfo, isAuthenticated } from '../utils/helpers';

const { Title, Text } = Typography;

const Login = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    ////console.log('Login component useEffect - checking auth status');
    // Nếu đã đăng nhập thì chuyển về dashboard
    if (isAuthenticated()) {
      ////console.log('User is already authenticated, navigating to dashboard');
      navigate('/');
    }
    
    // Test API connection
    const testAPI = async () => {
      try {
        ////console.log('Testing API connection...');
        await authService.testConnection();
        ////console.log('API connection successful!');
      } catch (error) {
        ////console.error('API connection failed:', error);
      }
    };
    
    testAPI();
  }, [navigate]);

  const onFinishFailed = (errorInfo) => {
    ////console.log('Form validation failed:', errorInfo);
  };

  const handleLogin = async (values) => {
    ////console.log('handleLogin function CALLED!');
    setLoading(true);
    try {
      ////console.log('Attempting login with:', values);
      const response = await authService.login(
        values.username, 
        values.password,
        'Admin Portal Web'
      );
      
      ////console.log('Login response:', response);

      if (response.success) {
        ////console.log('Processing successful login response:', response.data);
        
        // API trả về data là array, lấy phần tử đầu tiên
        const loginData = response.data[0] || response.data;
        
        // Lưu token
        if (loginData.access_token) {
          localStorage.setItem('accessToken', loginData.access_token);
          ////console.log('Token saved:', loginData.access_token);
        }
        
        // Lưu refresh token
        if (loginData.refresh_token) {
          localStorage.setItem('refreshToken', loginData.refresh_token);
          ////console.log('Refresh token saved:', loginData.refresh_token);
        }
        
        // Lưu thông tin user
        const userInfo = {
          user_id: loginData.user_id,
          username: loginData.username,
          role: loginData.role || 'admin',
          full_name: loginData.full_name || loginData.username
        };
        
        setUserInfo(userInfo);
        ////console.log('User info saved:', userInfo);
        
        message.success('Đăng nhập thành công!');
        
        // Debug: Log current state
        ////console.log('Navigating to dashboard...');
        //////console.log('Current auth state:', localStorage.getItem('accessToken'));
        
        // Small delay to ensure state is saved
        setTimeout(() => {
          ////console.log('Inside setTimeout: Navigating to /');
          navigate('/', { replace: true });
        }, 100);
      } else {
        ////console.log('Login failed, response:', response);
        message.error(response.message || 'Đăng nhập thất bại');
      }
    } catch (error) {
      ////console.error('Login error:', error);
      message.error(error.response?.data?.message || 'Đăng nhập thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ color: '#1890ff', marginBottom: 8 }}>
            Admin Portal
          </Title>
          <Text type="secondary">
            Hệ thống quản trị
          </Text>
        </div>
        {!showReset ? (
          <Spin spinning={loading}>
            <Form
              form={form}
              name="login"
              onFinish={handleLogin}
              onFinishFailed={onFinishFailed}
              layout="vertical"
              requiredMark={false}
            >
              <Form.Item
                name="username"
                label="Tên đăng nhập"
                rules={[{
                  required: true,
                  message: 'Vui lòng nhập tên đăng nhập!',
                }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Nhập tên đăng nhập"
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="password"
                label="Mật khẩu"
                rules={[{
                  required: true,
                  message: 'Vui lòng nhập mật khẩu!',
                }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Nhập mật khẩu"
                  size="large"
                />
              </Form.Item>
              <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  style={{ height: 48, fontSize: 16 }}
                >
                  Đăng nhập
                </Button>
              </Form.Item>
            </Form>
            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <Button type="link" style={{ padding: 0 }} onClick={() => setShowReset(true)}>
                Quên mật khẩu?
              </Button>
            </div>
          </Spin>
        ) : (
          <Spin spinning={resetLoading}>
            <Form
              layout="vertical"
              onFinish={async () => {
                if (!resetEmail) {
                  message.error('Vui lòng nhập email!');
                  return;
                }
                setResetLoading(true);
                try {
                  // Gọi API gửi email reset token
                  const response = await authService.resetToken(resetEmail);
                  if (response.success) {
                    message.success('Đã gửi email xác thực. Vui lòng kiểm tra hộp thư!');
                    setShowReset(false);
                  } else {
                    message.error(response.message || 'Không gửi được email.');
                  }
                } catch (error) {
                  message.error('Lỗi gửi email. Vui lòng thử lại.');
                } finally {
                  setResetLoading(false);
                }
              }}
            >
              <Form.Item label="Email nhận link xác thực" required>
                <Input
                  type="email"
                  placeholder="Nhập email quản trị viên"
                  size="large"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                />
              </Form.Item>
              <Form.Item style={{ marginTop: 32, marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={resetLoading}
                  style={{ height: 48, fontSize: 16 }}
                >
                  Gửi link xác thực
                </Button>
              </Form.Item>
              <div style={{ textAlign: 'right', marginTop: 12 }}>
                <Button type="link" style={{ padding: 0 }} onClick={() => setShowReset(false)}>
                  Quay lại đăng nhập
                </Button>
              </div>
            </Form>
          </Spin>
        )}
        <div style={{ 
          marginTop: 24, 
          padding: 16, 
          background: '#f8f9fa', 
          borderRadius: 8,
          textAlign: 'center' 
        }}>
          
        </div>
      </Card>
    </div>
  );
};

export default Login;
